import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import TableOperations from 'core-system/Table/TableOperationsUI'
import { FilterCheckbox, FilterObject } from 'core-system/Table/FilterModel'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import Box from 'core-system/Box/Box'
import MetricsGrid from 'features/Dashboard/components/MetricsGrid'

const Stats = styled.div`
  margin: 1.25rem 0 0.25rem;
`

const Metric = styled(Box)`
  padding: 1.5rem;
  height: 100%;
`

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1.25rem 0 1rem;
`

const formatMoney = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const formatDate = (value: string | null) => {
  if (!value) {
    return '—'
  }
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

export type ReportedUsageData = {
  hasProgram: boolean
  totalCents: number
  merchants: {
    merchant: string
    label: string
    spendCents: number
    records: number
    rows: {
      id: string
      occurredOn: string | null
      amountCents: number
      detail: string
    }[]
  }[]
}

type UsageRow = {
  id: string
  merchant: string
  merchantLabel: string
  occurredOn: string
  dateLabel: string
  detail: string
  amountCents: number
}

const HeaderCell = ({ text }: { text: string }) => (
  <TableRowCell noWrap>
    <Text variant='action4' fontWeight={500} color={palette.text.secondary}>
      {text}
    </Text>
  </TableRowCell>
)

const ReportedUsageContent = ({ data }: { data: ReportedUsageData }) => {
  const [searchValue, setSearchValue] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [selectedFilters, setSelectedFilters] = useState<FilterCheckbox[]>([])
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    'desc'
  )

  const rows = useMemo<UsageRow[]>(
    () =>
      data.merchants.flatMap((merchant) =>
        merchant.rows.map((row) => ({
          id: row.id,
          merchant: merchant.merchant,
          merchantLabel: merchant.label,
          occurredOn: row.occurredOn || '',
          dateLabel: formatDate(row.occurredOn),
          detail: row.detail || '—',
          amountCents: row.amountCents,
        }))
      ),
    [data.merchants]
  )

  const filters = useMemo<FilterObject[]>(() => {
    const merchants = [
      ...new Map(rows.map((row) => [row.merchant, row.merchantLabel])),
    ]
    if (!merchants.length) {
      return []
    }
    return [
      {
        header: 'Merchant',
        checkboxes: merchants.map(([id, label]) => ({
          label,
          checked: false,
          key: 'merchant',
          value: id,
        })),
      },
    ]
  }, [rows])

  const visibleRows = useMemo(() => {
    const needle = appliedSearch.trim().toLowerCase()
    const merchants = selectedFilters
      .filter((item) => item.key === 'merchant')
      .map((item) => String(item.value))
    let next = rows.filter((row) => {
      if (merchants.length && !merchants.includes(row.merchant)) {
        return false
      }
      if (!needle) {
        return true
      }
      return [row.merchantLabel, row.dateLabel, row.detail, formatMoney(row.amountCents)]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    })
    next = [...next].sort((a, b) => {
      const result = a.occurredOn.localeCompare(b.occurredOn)
      return sortDirection === 'asc' ? result : -result
    })
    return next
  }, [appliedSearch, rows, selectedFilters, sortDirection])

  if (!data.hasProgram) {
    return (
      <Text variant='body1' color={palette.text.secondary}>
        Your employer has not shared reported usage yet.
      </Text>
    )
  }
  if (!rows.length) {
    return (
      <Text variant='body1' color={palette.text.secondary}>
        No usage has been matched to your email yet.
      </Text>
    )
  }

  return (
    <>
      <Stats>
        <MetricsGrid
          spacing={2}
          noOfColumns={4}
          columns={[
            <Metric key='total'>
              <Text variant='h2' fontWeight={600} marginBottom='0.5rem'>
                {formatMoney(data.totalCents)}
              </Text>
              <Text variant='body1' color={palette.text.secondary}>
                Total
              </Text>
            </Metric>,
            ...data.merchants.map((merchant) => (
              <Metric key={merchant.merchant}>
                <Text variant='h2' fontWeight={600} marginBottom='0.5rem'>
                  {formatMoney(merchant.spendCents)}
                </Text>
                <Text variant='body1' color={palette.text.secondary}>
                  {merchant.label}
                </Text>
              </Metric>
            )),
          ]}
        />
      </Stats>
      <Toolbar>
        <TableOperations
          handleSearch={(value) => setAppliedSearch(value.trim())}
          handleSearchClear={() => {
            setSearchValue('')
            setAppliedSearch('')
          }}
          onAscendingClick={() => setSortDirection('asc')}
          onDescendingClick={() => setSortDirection('desc')}
          filters={filters}
          handleApplyFilters={setSelectedFilters}
          handleFiltersReset={() => setSelectedFilters([])}
          isFilterButtonActive={selectedFilters.length > 0}
          activeSortDirection={sortDirection}
          isSearchActive={Boolean(appliedSearch)}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
      </Toolbar>
      <Table
        maxRows={10}
        emptyMessage='No usage matches'
        showPagination={visibleRows.length > 10}
        headerCells={[
          <HeaderCell key='date' text='Date' />,
          <HeaderCell key='merchant' text='Merchant' />,
          <HeaderCell key='detail' text='Detail' />,
          <HeaderCell key='amount' text='Amount' />,
        ]}
        rows={visibleRows.map((row) => (
          <TableRow key={row.id}>
            <TableRowCell noWrap style={{ padding: '1rem' }}>
              <Text variant='action3'>{row.dateLabel}</Text>
            </TableRowCell>
            <TableRowCell noWrap style={{ padding: '1rem' }}>
              <Text variant='action3'>{row.merchantLabel}</Text>
            </TableRowCell>
            <TableRowCell style={{ padding: '1rem' }}>
              <Text variant='action3' color={palette.text.secondary}>
                {row.detail}
              </Text>
            </TableRowCell>
            <TableRowCell noWrap style={{ padding: '1rem' }}>
              <Text variant='action3' fontWeight={500}>
                {formatMoney(row.amountCents)}
              </Text>
            </TableRowCell>
          </TableRow>
        ))}
      />
    </>
  )
}

export default ReportedUsageContent
